import { figmaPxToWidthPercent } from '@ui/Report';

/**
 * Whether proposal charts should render target-specific UI.
 *
 * Zero remains a valid stored field value, but visually uses the absent-target
 * state (no guide line or delta chips).
 */
export const hasVisibleProposalTarget = (
  target: number | null | undefined,
): target is number => typeof target === 'number' && target !== 0;

/** CSS variable set on summary chart columns — holds the computed bar width expression. */
export const REPORT_BAR_WIDTH_VAR = '--report-bar-width';

/** Reference bar width in {@link Proposals} when the 150px cap applies. */
export const PROPOSALS_BAR_LAYOUT_REF_WIDTH_PX = 150;

/** Matches {@link Proposals} `MIN_BAR_WIDTH` on the 1920 Figma frame. */
const PROPOSALS_MIN_BAR_WIDTH_PX = 40;
const PROPOSALS_MIN_BAR_WIDTH_CQW = figmaPxToWidthPercent(
  PROPOSALS_MIN_BAR_WIDTH_PX,
);

/** Matches {@link Proposals} max bar width cap (150px on the 1920 Figma frame). */
const PROPOSALS_MAX_BAR_WIDTH_PX = PROPOSALS_BAR_LAYOUT_REF_WIDTH_PX;
const PROPOSALS_MAX_BAR_WIDTH_CQW = figmaPxToWidthPercent(
  PROPOSALS_MAX_BAR_WIDTH_PX,
);

/**
 * CSS width matching {@link Proposals}:
 * `max(40px, min((2 × width) / (3n + 1), min(width × 25%, 150px)))`.
 *
 * Uses mixed `cqw` + `px` for min/max caps — same as build tab where 150px is
 * absolute until the container is narrower than 600px, then 25% binds.
 *
 * @param barCount Includes baseline when present (same as report {@link ReportSummary}).
 */
export const getProposalsOverviewBarWidthCss = (barCount: number) => {
  const n = Math.max(barCount, 1);
  const fromFormulaCqw = (2 * 100) / (3 * n + 1);
  return `max(${PROPOSALS_MIN_BAR_WIDTH_PX}px, min(${fromFormulaCqw}cqw, min(25cqw, ${PROPOSALS_MAX_BAR_WIDTH_PX}px)))`;
};

/**
 * Pure cqw bar width for report sidebar thumbnails (~160px wide).
 * Px floors in {@link getProposalsOverviewBarWidthCss} overflow narrow containers.
 */
export const getProposalsOverviewBarWidthThumbnailCss = (barCount: number) => {
  const n = Math.max(barCount, 1);
  const fromFormulaCqw = (2 * 100) / (3 * n + 1);
  return `max(${PROPOSALS_MIN_BAR_WIDTH_CQW}cqw, min(${fromFormulaCqw}cqw, 25cqw, ${PROPOSALS_MAX_BAR_WIDTH_CQW}cqw))`;
};

/** Gap between bars — `barWidth / 2` in {@link Proposals}. */
export const getProposalsOverviewBarGapCss = (barWidthCss: string) =>
  `calc(${barWidthCss} / 2)`;

/** Gap when bar width is provided via {@link REPORT_BAR_WIDTH_VAR} on an ancestor. */
export const getProposalsOverviewBarGapFromVarCss = () =>
  `calc(var(${REPORT_BAR_WIDTH_VAR}) / 2)`;

/**
 * Width of the bar-foot rule — bar row plus {@link PROPOSALS_OVERVIEW_NAME_BAND.tabWidthFactor}
 * overhang on the outer edges (matches {@link OverviewBarShell} name tabs).
 */
export const getProposalsOverviewBarFootRuleWidthCss = (barCount: number) => {
  const n = Math.max(barCount, 1);
  const widthFactor =
    n + (n - 1) / 2 + PROPOSALS_OVERVIEW_NAME_BAND.tabWidthFactor - 1;
  return `calc(var(${REPORT_BAR_WIDTH_VAR}) * ${widthFactor})`;
};

/**
 * Scales a ProposalsOverview px measure relative to {@link REPORT_BAR_WIDTH_VAR}.
 * Requires an ancestor to set `--report-bar-width` to {@link getProposalsOverviewBarWidthCss}.
 */
export const barScaledCss = (px: number) =>
  `calc(var(${REPORT_BAR_WIDTH_VAR}) * ${px / PROPOSALS_BAR_LAYOUT_REF_WIDTH_PX})`;

/** Name band under each bar ({@link OverviewBarShell}) — px at 150px reference bar. */
export const PROPOSALS_OVERVIEW_NAME_BAND = {
  heightPx: 46,
  fontSizePx: 13,
  paddingXPx: 16,
  tabWidthFactor: 1.4,
} as const;

/** CO₂ bar face label ({@link VerticalBar} defaults). */
export const PROPOSALS_OVERVIEW_CO2_LABEL = {
  fontSizePx: 30,
  bottomPx: 22,
  minBarHeightPx: 36,
} as const;

/** Baseline's own main bar fill; backdrop and element-list baseline colors stay separate. */
export const PROPOSALS_OVERVIEW_BASELINE_BAR_COLOR = '#808080';

/** Target diff chip ({@link VerticalBar} / report-product-groups `REPORT_DIFF_CHIP`). */
export const PROPOSALS_OVERVIEW_DIFF_CHIP = {
  heightPx: 24,
  paddingXPx: 8,
  borderRadiusPx: 4,
  fontSizePx: 12,
  letterSpacingPx: 0.24,
  gapAboveBarPx: 24,
  barTopRadiusPx: 4,
  borderPx: 1,
} as const;

/** Target guide line — matches {@link Proposals} dashed line (`borderTop`, 12px hit area, −4px bottom). */
export const PROPOSALS_OVERVIEW_TARGET_LINE = {
  strokePx: 1,
  hitHeightPx: 12,
  offsetPx: 4,
} as const;

/** Min bar height ratio before CO₂ label is shown — `36px / 250px` reference slot. */
export const PROPOSALS_OVERVIEW_CO2_LABEL_MIN_HEIGHT_RATIO =
  PROPOSALS_OVERVIEW_CO2_LABEL.minBarHeightPx / 250;
